import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, field, IntegerDecoder, NumberDecoder, StringDecoder } from '@common/encoding';
import { isSimpleError, isSimpleErrors, SimpleError } from '@common/errors';
import { Formatter } from '@stamhoofd/utility';
import { ValidatedAddress } from '../addresses/Address.js';
import { compileToInMemoryFilter } from '../filters/InMemoryFilter.js';
import { checkoutInMemoryFilterCompilers } from '../filters/inMemoryFilterDefinitions.js';
import { RecordAnswerDecoder, RecordAnswerMapDecoder } from '../members/records/RecordAnswer.js';
import { RecordCategory } from '../members/records/RecordCategory.js';
import { PaymentMethod } from '../PaymentMethod.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
import { Cart } from './Cart.js';
import { Customer } from './Customer.js';
import { CustomerFieldRequirement } from './CustomerFieldRequirement.js';
import { Discount } from './Discount.js';
import { DiscountCode } from './DiscountCode.js';
import { WebshopFieldAnswer } from './WebshopField.js';
import { AnyCheckoutMethodDecoder, CheckoutMethodType, WebshopDeliveryMethod, WebshopTimeSlot } from './WebshopMetaData.js';
export class Checkout extends AutoEncoder {
    timeSlot = null;
    checkoutMethod = null;
    /**
     * Only needed for delivery
     */
    address = null;
    customer = Customer.create({});
    /**
     * Cart item whose participant is also the contact person of the order. The details collected for that
     * participant are copied to `customer` instead of asking them again (see `customerFieldsFromCartItem`).
     */
    customerCartItemId = null;
    fieldAnswers = [];
    recordAnswers = new Map();
    cart = Cart.create({});
    paymentMethod = null;
    administrationFee = 0;
    discounts = [];
    discountCodes = [];
    /**
     * Applied fixed discount (not applicable to a specific cart item)
     */
    fixedDiscount = 0;
    /**
     * Applied percentage discount (not applicable to a specific cart item)
     * in pertenthousand
     */
    percentageDiscount = 0;
    /**
     * Number of persons we did reserve in webshop time slots (and maybe future other maximums)
     */
    reservedPersons = 0;
    /**
     * Whether we reserved order stock in webshop time slots (and maybe future other maximums)
     */
    reservedOrder = false;
    get paymentContext() {
        if (this.checkoutMethod?.type === CheckoutMethodType.Takeout) {
            return 'takeout';
        }
        if (this.checkoutMethod?.type === CheckoutMethodType.Delivery) {
            return 'delivery';
        }
        return null;
    }
    get deliveryPrice() {
        if (!this.checkoutMethod || this.checkoutMethod.type !== CheckoutMethodType.Delivery) {
            return 0;
        }
        if (!(this.checkoutMethod instanceof WebshopDeliveryMethod)) {
            // will throw in validation
            return 0;
        }
        if (this.checkoutMethod.price.minimumPrice !== null && this.cart.price >= this.checkoutMethod.price.minimumPrice) {
            return this.checkoutMethod.price.discountPrice;
        }
        return this.checkoutMethod.price.price;
    }
    get appliedPercentageDiscount() {
        return 100 * Math.round(this.cart.price * this.percentageDiscount / 10000_00);
    }
    get totalPrice() {
        // Percentage discount
        // + this.administrationFee;
        return Math.max(0, this.cart.price - this.appliedPercentageDiscount - this.fixedDiscount) + this.deliveryPrice + this.administrationFee;
    }
    get amount() {
        return this.cart.items.reduce((acc, item) => acc + item.amount, 0);
    }
    get priceBreakown() {
        const all = [
            {
                name: Formatter.percentage(this.percentageDiscount) + ' ' + $t('%sT'),
                price: -this.appliedPercentageDiscount,
            },
            {
                name: $t('%176'),
                price: -this.fixedDiscount,
            },
            {
                name: $t('%Sn'),
                price: this.deliveryPrice,
            },
            {
                name: $t('%qs'),
                price: this.administrationFee,
            },
        ].filter(a => a.price !== 0);
        if (all.length > 0) {
            all.unshift({
                name: $t('%xJ'),
                price: this.cart.priceWithDiscounts,
            });
        }
        return [
            ...all,
            {
                name: $t('%xL'),
                price: this.totalPrice,
            },
        ];
    }
    get totalPriceWithoutAdministrationFee() {
        return this.totalPrice - this.administrationFee;
    }
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, checkoutInMemoryFilterCompilers);
            return compiledFilter(this);
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
    isRecordEnabled(_record) {
        return true;
    }
    getRecordAnswers() {
        return this.recordAnswers;
    }
    patchRecordAnswers(patch) {
        return this.patch({
            recordAnswers: patch,
        });
    }
    validateAnswers(webshop) {
        const newAnswers = [];
        for (const field of webshop.meta.customFields) {
            const answer = this.fieldAnswers.find(a => a.field.id === field.id);
            try {
                if (!answer) {
                    const a = WebshopFieldAnswer.create({ field, answer: '' });
                    a.validate();
                    newAnswers.push(a);
                }
                else {
                    answer.field = field;
                    answer.validate();
                    newAnswers.push(answer);
                }
            }
            catch (e) {
                if (isSimpleError(e) || isSimpleErrors(e)) {
                    e.addNamespace('fieldAnswers.' + field.id);
                }
                throw e;
            }
        }
        this.fieldAnswers = newAnswers;
    }
    validateCart(webshop, organizationMeta, asAdmin = false, options = {}) {
        if (this.cart.items.length === 0) {
            throw new SimpleError({
                code: 'cart_empty',
                message: 'Cart is empty',
                human: $t('%sU'),
                field: 'cart',
            });
        }
        const totalItems = this.cart.items.reduce((a, b) => a + b.amount, 0);
        if (totalItems > 1000) {
            throw new SimpleError({
                code: 'too_many_items',
                message: 'Too many items',
                human: 'Je kan maximaal 1000 items tegelijkertijd bestellen',
                field: 'cart',
            });
        }
        try {
            this.cart.validate(webshop, asAdmin, options);
        }
        catch (e) {
            if (isSimpleError(e) || isSimpleErrors(e)) {
                e.addNamespace('cart');
            }
            // also update discounts on errors
            this.updateDiscounts(webshop);
            this.calculatePrices();
            throw e;
        }
        this.updateDiscounts(webshop);
        this.calculatePrices();
        if (!asAdmin && webshop.meta.availableUntil && webshop.meta.availableUntil < new Date()) {
            throw new SimpleError({
                code: 'closed',
                message: 'Orders are closed',
                human: $t('%sV'),
            });
        }
        if (!asAdmin && webshop.meta.openAt && webshop.meta.openAt > new Date()) {
            throw new SimpleError({
                code: 'closed',
                message: 'Orders are closed',
                human: $t('%sW', { date: Formatter.dateTime(webshop.meta.openAt) }),
            });
        }
        if (!asAdmin && webshop.isClosed()) {
            throw new SimpleError({
                code: 'closed',
                message: 'Orders are closed',
                human: $t('%sX'),
            });
        }
    }
    validateCheckoutMethod(webshop, organizationMeta) {
        if (this.checkoutMethod === null) {
            if (webshop.meta.checkoutMethods.length > 0) {
                throw new SimpleError({
                    code: 'missing_checkout_method',
                    message: 'Checkout method is required',
                    human: $t('%sY'),
                    field: 'checkoutMethod',
                });
            }
            return;
        }
        const current = this.checkoutMethod;
        const checkoutMethod = webshop.meta.checkoutMethods.find(m => m.id === current.id);
        if (!checkoutMethod) {
            if (webshop.meta.checkoutMethods.length === 0) {
                this.checkoutMethod = null;
                return;
            }
            throw new SimpleError({
                code: 'invalid_checkout_method',
                message: 'Checkout method is invalid',
                human: $t('%sY'),
                field: 'checkoutMethod',
            });
        }
        this.checkoutMethod = checkoutMethod;
    }
    validateDeliveryAddress(webshop, organizationMeta) {
        if (!this.checkoutMethod || this.checkoutMethod.type !== CheckoutMethodType.Delivery) {
            this.address = null;
            return;
        }
        if (!(this.checkoutMethod instanceof WebshopDeliveryMethod)) {
            throw new SimpleError({
                code: 'invalid_data_type',
                message: 'Invalid data type',
                human: $t('%sZ'),
                field: 'address',
            });
        }
        if (!this.address) {
            throw new SimpleError({
                code: 'missing_address',
                message: 'Checkout address is invalid',
                human: $t('%sa'),
                field: 'address',
            });
        }
        // Always store the delivery address on the customer too (even if addressEnabled is false),
        // so we don't ask for the same address twice.
        this.customer.address = this.address;
        // Check country
        if (this.checkoutMethod.countries.includes(this.address.country)) {
            return;
        }
        if (this.checkoutMethod.provinces.map(p => p.id).includes(this.address.provinceId)) {
            return;
        }
        if (this.checkoutMethod.cities.map(c => c.id).includes(this.address.cityId)) {
            return;
        }
        if (this.address.parentCityId && this.checkoutMethod.cities.map(c => c.id).includes(this.address.parentCityId)) {
            return;
        }
        throw new SimpleError({
            code: 'region_not_supported',
            message: 'Region not supported',
            human: $t('%sb'),
            field: 'address',
        });
    }
    validateTimeSlot(_webshop, _organizationMeta) {
        if (!this.checkoutMethod || this.checkoutMethod.timeSlots.timeSlots.length === 0) {
            this.timeSlot = null;
            return;
        }
        if (!this.timeSlot) {
            throw new SimpleError({
                code: 'missing_timeslot',
                message: 'Checkout timeslot is missings',
                human: $t('%sc'),
                field: 'timeSlot',
            });
        }
        const current = this.timeSlot;
        const timeSlot = this.checkoutMethod.timeSlots.timeSlots.find(s => s.id === current.id);
        const availableTimeslots = this.checkoutMethod.timeSlots.timeSlots.length;
        if (!timeSlot) {
            throw new SimpleError({
                code: 'invalid_timeslot',
                message: 'Checkout timeslot is invalid',
                human: $t('%sd'),
                field: 'timeSlot',
            });
        }
        // Check maximum
        if (!this.reservedOrder && timeSlot.remainingOrders === 0) {
            throw new SimpleError({
                code: 'timeslot_full',
                message: 'Timeslot has reached maximum orders',
                human: (availableTimeslots !== 1 ? $t('%se') : $t('%sf')),
                field: 'timeSlot',
            });
        }
        // Check maximum
        if (timeSlot.remainingPersons !== null && this.cart.persons - this.reservedPersons > timeSlot.remainingPersons) {
            const remainingPersons = timeSlot.remainingPersons;
            if (remainingPersons === 0) {
                throw new SimpleError({
                    code: 'timeslot_full',
                    message: 'Timeslot has reached maximum orders',
                    human: (availableTimeslots !== 1 ? $t('%se') : $t('%sf')),
                    field: 'timeSlot',
                });
            }
            throw new SimpleError({
                code: 'timeslot_full',
                message: 'Timeslot has reached maximum persons',
                // todo translation
                human: $t('%sg') + ' ' + (remainingPersons !== 1 ? $t('%bR') : $t('%1q')) + ' ' + $t('%sh') + ' ' + remainingPersons + ' ' + (remainingPersons !== 1 ? $t('%UL') : $t('%12Y')) + ' ' + $t('%si') + ' ' + (availableTimeslots !== 1 ? $t('%sj') : $t('%sk')) + $t('%sl') + ' ' + this.cart.persons + ' ' + (this.cart.persons !== 1 ? $t('%12R') : $t('%12P')) + (availableTimeslots !== 1 ? $t('%sm') : ''),
                field: 'timeSlot',
            });
        }
        this.timeSlot = timeSlot;
    }
    /**
     * Participants that can be the contact person of the order: children under 16 are left out.
     * Without a birth day we assume an adult.
     */
    get contactPersonCandidates() {
        return this.cart.items.filter((i) => {
            const age = i.customer?.age ?? null;
            return i.customer !== null && (age === null || age >= Checkout.contactPersonMinimumAge);
        });
    }
    static contactPersonMinimumAge = 16;
    /**
     * A single participant is only made the contact person without asking from this age on.
     * Younger candidates can still be picked in the customer step.
     */
    static automaticContactPersonMinimumAge = 18;
    get customerCartItem() {
        if (this.customerCartItemId === null) {
            return null;
        }
        return this.contactPersonCandidates.find(i => i.id === this.customerCartItemId) ?? null;
    }
    /**
     * Customer fields that are already collected on the contact person's cart item, so they are not asked again for the order.
     * A delivery address always wins over the participant's address.
     */
    get customerFieldsFromCartItem() {
        const fields = this.customerCartItem?.collectedCustomerFields ?? [];
        if (this.checkoutMethod?.type === CheckoutMethodType.Delivery) {
            return fields.filter(key => key !== 'address');
        }
        return fields;
    }
    /**
     * Preselects the first adult participant as contact person, as long as nobody was chosen and nothing was filled in yet.
     * Younger candidates stay available, but are never chosen without asking.
     */
    selectDefaultContactPerson() {
        // A contact person whose cart item was removed is forgotten first, so a new default can take over
        this.syncCustomerFromCartItem();
        if (this.customerCartItem || this.customer.hasValue('name') || this.customer.hasValue('email')) {
            return;
        }
        const item = this.defaultContactPerson;
        if (item) {
            this.customerCartItemId = item.id;
            this.syncCustomerFromCartItem();
        }
    }
    /**
     * The participant that becomes the contact person when nobody was chosen: the first adult.
     * Younger candidates stay available, but are never chosen without asking.
     */
    get defaultContactPerson() {
        return this.contactPersonCandidates.find(i => Checkout.canBeAutomaticContactPerson(i)) ?? null;
    }
    /**
     * Whether a participant may become the contact person without asking: adults only, and without a birth day we assume an adult.
     */
    static canBeAutomaticContactPerson(item) {
        const age = item.customer?.age ?? null;
        return item.customer !== null && (age === null || age >= Checkout.automaticContactPersonMinimumAge);
    }
    /**
     * Makes a participant the contact person, or nobody (null). The details copied from the previous
     * contact person are cleared first, so they can't end up on the order of someone else. What was
     * filled in for the order itself stays.
     */
    setContactPerson(cartItemId) {
        this.customer.copyFields(Customer.create({}), this.customerFieldsFromCartItem);
        this.customerCartItemId = cartItemId;
        this.syncCustomerFromCartItem();
    }
    /**
     * A logged in user is the contact person: forgets a participant chosen before logging in, with the details copied from them
     */
    forgetContactPerson() {
        if (this.customerCartItemId !== null) {
            this.customerCartItemId = null;
            this.customer = Customer.create({});
        }
    }
    /**
     * Copies the contact person's details to the order customer, so edits to the cart item carry over.
     * When that cart item is no longer in the cart or no longer qualifies, the contact person is forgotten,
     * together with everything filled in for them, so a new participant can take over.
     */
    syncCustomerFromCartItem() {
        const item = this.customerCartItem;
        if (!item) {
            if (this.customerCartItemId !== null) {
                this.customerCartItemId = null;
                this.customer = Customer.create({});
            }
            return;
        }
        this.customer.copyFields(item.customer, this.customerFieldsFromCartItem);
    }
    validateCustomer(webshop, organizationMeta, i18n, asAdmin = false, user = null) {
        if (user) {
            // The account is the contact person, even when a participant was chosen before logging in
            this.forgetContactPerson();
        }
        this.syncCustomerFromCartItem();
        if (user) {
            // An account without a full name fills it in during the checkout instead
            if (user.firstName && user.lastName) {
                this.customer.firstName = user.firstName;
                this.customer.lastName = user.lastName;
            }
            this.customer.email = user.email;
        }
        const settings = webshop.meta.customerSettings;
        this.customer.validate(
        // A delivery address is always stored on the customer, so never clear it in that case
        settings.address === CustomerFieldRequirement.Disabled && this.address
            ? settings.patch({ address: CustomerFieldRequirement.Optional })
            : settings, { asAdmin });
        this.validateAnswers(webshop);
        // Needed when only the customer step is validated: a full validate() checks this category again in validateRecordAnswers.
        // Admins may edit orders placed before the questions changed.
        if (settings.recordCategory && !asAdmin) {
            RecordCategory.validate([settings.recordCategory], this);
        }
    }
    validatePayment(webshop, organizationMeta) {
        if (!this.paymentMethod) {
            throw new SimpleError({
                code: 'missing_payment_method',
                message: 'Missing payment method',
                human: $t('%eU'),
                field: 'paymentMethod',
            });
        }
        if (!webshop.meta.paymentMethods.includes(this.paymentMethod)) {
            throw new SimpleError({
                code: 'invalid_payment_method',
                message: 'Invalid payment method',
                human: $t('%sS'),
                field: 'paymentMethod',
            });
        }
    }
    validateRecordAnswersFor(webshop, category) {
        RecordCategory.validate([category], this);
    }
    validateRecordAnswers(webshop, asAdmin = false) {
        const filtered = RecordCategory.removeOldAnswers(webshop.meta.allRecordCategories, this);
        this.recordAnswers = filtered.getRecordAnswers();
        // Admins may edit orders placed before the questions changed
        if (!asAdmin) {
            RecordCategory.validate(webshop.meta.allRecordCategories, this);
        }
    }
    updateAdministrationFee(webshop) {
        this.administrationFee = webshop.meta.paymentConfiguration.administrationFee.calculate(this.totalPriceWithoutAdministrationFee);
    }
    updateDiscounts(webshop) {
        this.discounts = webshop.meta.defaultDiscounts.slice();
        this.discounts.push(...this.discountCodes.flatMap(c => c.discounts));
    }
    calculatePrices() {
        // Group discounts by discounts that can get applied together with other discounts
        // for now: everything can get combined
        const discountOrders = [this.discounts];
        for (const discounts of discountOrders) {
            this.fixedDiscount = 0;
            this.percentageDiscount = 0;
            for (const item of this.cart.items) {
                item.discounts = [];
                // Reset all discounts on this item
                item.calculatePrices(this.cart);
            }
            const trackers = [];
            for (const discount of discounts) {
                trackers.push(...discount.applyToCheckout(this));
            }
            // Loop trackers and apply the one with the current highest potential
            while (true) {
                let bestPotential = null;
                for (const tracker of trackers) {
                    const potential = tracker.getPotentialDiscount();
                    if (potential !== 0 && (bestPotential === null || potential > bestPotential.potential)) {
                        bestPotential = { tracker, potential };
                    }
                }
                if (bestPotential) {
                    bestPotential.tracker.apply();
                }
                else {
                    break;
                }
            }
        }
    }
    /**
     * A copy with the items with the same code combined (see Cart.grouped), with its prices calculated again
     */
    withGroupedItems(webshop) {
        const checkout = this.clone();
        checkout.cart = checkout.cart.grouped();
        checkout.update(webshop);
        return checkout;
    }
    update(webshop) {
        this.updateDiscounts(webshop);
        this.calculatePrices();
        this.updateAdministrationFee(webshop);
    }
    validate(webshop, organizationMeta, i18n, asAdmin = false, user = null) {
        try {
            this.validateCart(webshop, organizationMeta, asAdmin);
            this.validateCheckoutMethod(webshop, organizationMeta);
            this.validateDeliveryAddress(webshop, organizationMeta);
            this.validateTimeSlot(webshop, organizationMeta);
            this.validateCustomer(webshop, organizationMeta, i18n, asAdmin, user);
            this.validateRecordAnswers(webshop, asAdmin);
            if (this.totalPrice !== 0 && !asAdmin) {
                this.validatePayment(webshop, organizationMeta);
            }
            else if (this.totalPrice === 0) {
                this.paymentMethod = PaymentMethod.Unknown;
            }
        }
        finally {
            this.update(webshop);
        }
    }
    /**
     * Convenience mapper
     */
    get deliveryMethod() {
        if (!this.checkoutMethod || this.checkoutMethod.type !== CheckoutMethodType.Delivery) {
            return null;
        }
        if (!(this.checkoutMethod instanceof WebshopDeliveryMethod)) {
            // will throw in validation
            return null;
        }
        return this.checkoutMethod;
    }
}
__decorate([
    field({ decoder: WebshopTimeSlot, nullable: true })
], Checkout.prototype, "timeSlot", void 0);
__decorate([
    field({ decoder: AnyCheckoutMethodDecoder, nullable: true })
], Checkout.prototype, "checkoutMethod", void 0);
__decorate([
    field({ decoder: ValidatedAddress, nullable: true })
], Checkout.prototype, "address", void 0);
__decorate([
    field({ decoder: Customer, version: 40 })
], Checkout.prototype, "customer", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 425 })
], Checkout.prototype, "customerCartItemId", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopFieldAnswer), version: 94 })
], Checkout.prototype, "fieldAnswers", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordAnswerDecoder), optional: true }),
    field({
        decoder: RecordAnswerMapDecoder,
        version: 252,
        optional: true,
        upgrade: (old) => {
            const map = new Map();
            for (const answer of old) {
                map.set(answer.settings.id, answer);
            }
            return map;
        },
    })
], Checkout.prototype, "recordAnswers", void 0);
__decorate([
    field({ decoder: Cart })
], Checkout.prototype, "cart", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethod), nullable: true })
], Checkout.prototype, "paymentMethod", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 207 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Checkout.prototype, "administrationFee", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Discount), version: 235 })
], Checkout.prototype, "discounts", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(DiscountCode), version: 239 })
], Checkout.prototype, "discountCodes", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 235 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], Checkout.prototype, "fixedDiscount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 235 })
], Checkout.prototype, "percentageDiscount", void 0);
__decorate([
    field({ decoder: NumberDecoder, version: 143 })
], Checkout.prototype, "reservedPersons", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 143 })
], Checkout.prototype, "reservedOrder", void 0);
//# sourceMappingURL=Checkout.js.map